import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Stack,
  Typography,
  Box,
  Fade,
  alpha,
  useTheme,
  Button,
  Paper,
  IconButton,
  Chip,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import HomeTwoToneIcon from '@mui/icons-material/HomeTwoTone';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LaptopIcon from '@mui/icons-material/Laptop';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DescriptionIcon from '@mui/icons-material/Description';
import { AuthContext } from '../../AuthContext';
import { useApi } from '../../hooks/useApi';

const AUDIT_RUN_ID = 'WINDOWS-ENDPOINT-AUDIT';

// The five checks written by Test-WindowsEndpointHealth.ps1, in display order.
const WINDOWS_CHECK_KEYS = [
  'agentOnline',
  'patchCompliance',
  'diskHealth',
  'antivirusHealth',
  'bitlockerStatus',
];

function WindowsEndpointAudit() {
  const { user } = useContext(AuthContext);
  const theme = useTheme();
  const jApi = useApi();
  const navigate = useNavigate();

  const [auditResults, setAuditResults] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [restartingAudit, setRestartingAudit] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const fetchAuditData = async () => {
    try {
      setErrorMessage("");
      setIsLoading(true);

      const response = await jApi.request('GET', `cma/audit?auditRunId=${AUDIT_RUN_ID}`);

      console.log('Audit response:', response);

      if (response.data && Array.isArray(response.data)) {
        setAuditResults(response.data);
      } else {
        setAuditResults([]);
      }
    } catch (ex) {
      console.error('Failed to fetch audit data:', ex);
      setErrorMessage(ex.toString());
      setAuditResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAuditData();
  }, []);

  const handleRefresh = () => {
    fetchAuditData();
  };

  const handleBack = () => {
    navigate('/operation');
  };

  const handleRestartAudit = async (result) => {
    const deviceUid = result.jobResults?.deviceUid;
    const account = result.account || result.jobResults?.account;

    if (!deviceUid) {
      setSnackbarMessage('Cannot restart audit: Device UID not found');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      setRestartingAudit(prev => ({ ...prev, [result.id]: true }));

      // Create VES job — re-run the per-device health worker. The worker resolves
      // the device's site from the account when no SiteUid is supplied.
      const vesJobPayload = {
        functionpath: result.functionPath,
        splat: {
          DeviceUid: deviceUid,
          AuditId: result.id,
          Account: account,
        },
      };

      console.log('Creating VES job for Windows endpoint audit:', vesJobPayload);
      const vesResponse = await jApi.request('POST', 'ves/job', vesJobPayload);
      console.log('VES job created:', vesResponse);

      const jobId = vesResponse.data?.id;

      if (jobId) {
        // Update audit record with jobId and reset to PENDING
        const updatePayload = {
          id: result.id,
          jobId: jobId,
          answer: 'PENDING',
        };

        console.log('Updating audit record:', updatePayload);
        await jApi.request('PUT', 'cma/audit', updatePayload);

        setSnackbarMessage(`Audit queued successfully for ${result.title || deviceUid}. Job ID: ${jobId}`);
        setSnackbarSeverity('success');
        setSnackbarOpen(true);

        // Refresh audit data after a short delay
        setTimeout(() => {
          fetchAuditData();
        }, 1000);
      } else {
        throw new Error('No job ID returned from VES');
      }
    } catch (error) {
      console.error('Failed to restart audit:', error);
      setSnackbarMessage('Failed to queue audit: ' + (error.message || 'Unknown error'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setRestartingAudit(prev => ({ ...prev, [result.id]: false }));
    }
  };

  const getStatusColor = (status) => {
    if (status === 'PASS') return theme.palette.success.main;
    if (status === 'WARNING') return theme.palette.warning.main;
    if (status === 'FAIL') return theme.palette.error.main;
    return theme.palette.text.secondary;
  };

  const getStatusIcon = (status) => {
    if (status === 'PASS') return <CheckCircleIcon />;
    if (status === 'WARNING') return <WarningAmberIcon />;
    if (status === 'FAIL') return <ErrorIcon />;
    return <ErrorIcon />;
  };

  // Check-level result colors/icons. Worker check results are PASS / WARN / FAIL.
  const getCheckColor = (checkResult) => {
    if (checkResult === 'PASS') return theme.palette.success.main;
    if (checkResult === 'WARN') return theme.palette.warning.main;
    if (checkResult === 'FAIL') return theme.palette.error.main;
    return theme.palette.text.secondary;
  };

  const getCheckIcon = (checkResult) => {
    if (checkResult === 'PASS') return <CheckCircleIcon sx={{ fontSize: 20 }} />;
    if (checkResult === 'WARN') return <WarningAmberIcon sx={{ fontSize: 20 }} />;
    if (checkResult === 'FAIL') return <ErrorIcon sx={{ fontSize: 20 }} />;
    return <ErrorIcon sx={{ fontSize: 20 }} />;
  };

  // "agentOnline" -> "Agent Online"
  const formatCheckName = (checkKey) =>
    checkKey.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());

  const getSummary = () => {
    const total = auditResults.length;
    const passed = auditResults.filter(r => r.answer === 'PASS').length;
    const warning = auditResults.filter(r => r.answer === 'WARNING').length;
    const failed = auditResults.filter(r => r.answer === 'FAIL').length;

    const allIssues = auditResults.reduce((acc, result) => {
      if (result.jobResults && result.jobResults.checks) {
        const failedChecks = WINDOWS_CHECK_KEYS.filter(
          (key) => result.jobResults.checks[key] && result.jobResults.checks[key].passed === false
        ).length;
        return acc + failedChecks;
      }
      return acc;
    }, 0);

    return { total, passed, warning, failed, allIssues };
  };

  const filteredResults = auditResults.filter(result => {
    const matchesSearch = result.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         result.jobResults?.deviceUid?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         result.account?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter = statusFilter === 'ALL' || result.answer === statusFilter;

    return matchesSearch && matchesFilter;
  });

  const summary = getSummary();

  // Gradient header style
  const headerGradient = {
    background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.1 : 0.2)}`,
    position: 'relative',
    overflow: 'hidden',
    boxShadow: theme.palette.mode === 'dark'
      ? `0 4px 12px ${alpha(theme.palette.common.black, 0.3)}`
      : `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
  };

  return (
    <Box sx={{ minHeight: '100vh', pb: 4 }}>
      <Container maxWidth="xl">
        <Fade in timeout={500}>
          <Box sx={{ pt: 3, pb: 2 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: 2,
                background: alpha(theme.palette.background.paper, 0.6),
                backdropFilter: 'blur(10px)',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <Link to="/home" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.5,
                    borderRadius: 1.5,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: alpha(theme.palette.secondary.main, 0.1),
                    },
                  }}
                >
                  <HomeTwoToneIcon sx={{ fontSize: 20, color: theme.palette.secondary.main }} />
                  <Typography variant="body2" sx={{ color: theme.palette.secondary.main, fontWeight: 500 }}>
                    Home
                  </Typography>
                </Box>
              </Link>
              <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>
                /
              </Typography>
              <Link to="/operation" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    px: 1,
                    py: 0.5,
                    borderRadius: 1.5,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: alpha(theme.palette.secondary.main, 0.1),
                      color: theme.palette.secondary.main,
                    },
                  }}
                >
                  Operation Center
                </Typography>
              </Link>
              <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>
                /
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 600, px: 1, py: 0.5 }}>
                Windows Endpoint Audit
              </Typography>
            </Box>
          </Box>
        </Fade>

        {/* Hero Section */}
        <Fade in timeout={600}>
          <Box sx={{ ...headerGradient, p: 4, mb: 4 }}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 8 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: 3,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.3)} 0%, ${alpha(theme.palette.info.main, 0.1)} 100%)`,
                      boxShadow: `0 8px 16px ${alpha(theme.palette.info.main, 0.3)}`,
                      border: `2px solid ${alpha(theme.palette.info.main, 0.3)}`,
                    }}
                  >
                    <LaptopIcon sx={{ fontSize: 48, color: 'info.main' }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 700,
                        background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.primary.main} 100%)`,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      Windows Endpoint Audit
                    </Typography>
                    <Typography variant="h6" color="text.secondary">
                      NOC Endpoint Health Compliance Review
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-end">
                  <IconButton
                    onClick={() => {
                      const url = `/audit/runbook?filePath=${encodeURIComponent('cma/audit/system/Test-WindowsEndpointHealth.md')}`;
                      window.open(url, '_blank');
                    }}
                    sx={{
                      background: alpha(theme.palette.info.main, 0.1),
                      '&:hover': { background: alpha(theme.palette.info.main, 0.2) },
                    }}
                    title="View Documentation"
                  >
                    <DescriptionIcon />
                  </IconButton>
                  <IconButton
                    onClick={handleBack}
                    sx={{
                      background: alpha(theme.palette.secondary.main, 0.1),
                      '&:hover': { background: alpha(theme.palette.secondary.main, 0.2) },
                    }}
                  >
                    <ArrowBackIcon />
                  </IconButton>
                  <IconButton
                    onClick={handleRefresh}
                    disabled={isLoading}
                    sx={{
                      background: alpha(theme.palette.primary.main, 0.1),
                      '&:hover': { background: alpha(theme.palette.primary.main, 0.2) },
                    }}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Stack>
              </Grid>
            </Grid>
            {isLoading && <LinearProgress sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }} />}
          </Box>
        </Fade>

        {/* Summary Cards */}
        <Fade in timeout={700}>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Paper
                sx={{
                  p: 2.5,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.08)} 0%, ${alpha(theme.palette.info.main, 0.03)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                  borderRadius: 2,
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                  Total Devices
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 700, color: 'info.main', mt: 0.5 }}>
                  {summary.total}
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Paper
                sx={{
                  p: 2.5,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)} 0%, ${alpha(theme.palette.success.main, 0.03)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                  borderRadius: 2,
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                  Passed
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 700, color: 'success.main', mt: 0.5 }}>
                  {summary.passed}
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Paper
                sx={{
                  p: 2.5,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.08)} 0%, ${alpha(theme.palette.warning.main, 0.03)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                  borderRadius: 2,
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                  Warnings
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 700, color: 'warning.main', mt: 0.5 }}>
                  {summary.warning}
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Paper
                sx={{
                  p: 2.5,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.08)} 0%, ${alpha(theme.palette.error.main, 0.03)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                  borderRadius: 2,
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                  Failures
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 700, color: 'error.main', mt: 0.5 }}>
                  {summary.failed}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Fade>

        {/* Search and Filter Bar */}
        <Fade in timeout={800}>
          <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
              <TextField
                fullWidth
                placeholder="Search by hostname or account..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ flex: 1 }}
              />
              <ToggleButtonGroup
                value={statusFilter}
                exclusive
                onChange={(e, newValue) => newValue && setStatusFilter(newValue)}
                size="small"
              >
                <ToggleButton value="ALL">All</ToggleButton>
                <ToggleButton value="FAIL" sx={{ color: 'error.main' }}>Failed</ToggleButton>
                <ToggleButton value="WARNING" sx={{ color: 'warning.main' }}>Warning</ToggleButton>
                <ToggleButton value="PASS" sx={{ color: 'success.main' }}>Passed</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Paper>
        </Fade>

        {/* Error Message */}
        {errorMessage && (
          <Fade in>
            <Paper
              sx={{
                p: 3,
                mb: 3,
                background: alpha(theme.palette.error.main, 0.1),
                border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                borderRadius: 2,
              }}
            >
              <Typography variant="body1" color="error.main" sx={{ fontWeight: 500 }}>
                {errorMessage}
              </Typography>
            </Paper>
          </Fade>
        )}

        {/* Results */}
        {filteredResults.length > 0 ? (
          <Stack spacing={2}>
            {filteredResults
              .sort((a, b) => {
                // Sort by status: FAIL first, then WARNING, then PASS
                const statusOrder = { FAIL: 0, WARNING: 1, PASS: 2 };
                return statusOrder[a.answer] - statusOrder[b.answer];
              })
              .map((result, idx) => {
                const checks = result.jobResults?.checks || {};
                const issueCount = WINDOWS_CHECK_KEYS.filter(
                  (key) => checks[key] && checks[key].passed === false
                ).length;

                return (
                  <Fade in timeout={900 + idx * 50} key={result.id}>
                    <Accordion
                      defaultExpanded={false}
                      sx={{
                        border: `2px solid ${alpha(getStatusColor(result.answer), 0.3)}`,
                        borderRadius: 2,
                        '&:before': { display: 'none' },
                        boxShadow: `0 2px 8px ${alpha(getStatusColor(result.answer), 0.15)}`,
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          background: `linear-gradient(135deg, ${alpha(getStatusColor(result.answer), 0.08)} 0%, ${alpha(getStatusColor(result.answer), 0.03)} 100%)`,
                        }}
                      >
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', pr: 2 }}>
                          <Box
                            sx={{
                              width: 56,
                              height: 56,
                              borderRadius: 2,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: alpha(getStatusColor(result.answer), 0.15),
                              color: getStatusColor(result.answer),
                            }}
                          >
                            {getStatusIcon(result.answer)}
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                              {result.title}
                            </Typography>
                            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                              <Chip
                                label={result.account || 'N/A'}
                                size="small"
                                sx={{ fontFamily: 'monospace', fontWeight: 600 }}
                              />
                              <Chip
                                label={result.answer}
                                size="small"
                                sx={{
                                  bgcolor: alpha(getStatusColor(result.answer), 0.2),
                                  color: getStatusColor(result.answer),
                                  fontWeight: 700,
                                }}
                              />
                              {issueCount > 0 && (
                                <Chip
                                  icon={<ErrorIcon />}
                                  label={`${issueCount} issue${issueCount !== 1 ? 's' : ''}`}
                                  size="small"
                                  color="error"
                                  sx={{ fontWeight: 600 }}
                                />
                              )}
                              <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', ml: 1 }}>
                                {result.jobResults?.timestamp && new Date(result.jobResults.timestamp).toLocaleString()}
                              </Typography>
                            </Stack>
                          </Box>
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails>
                        {/* Restart Audit Button */}
                        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            startIcon={restartingAudit[result.id] ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
                            onClick={() => handleRestartAudit(result)}
                            disabled={restartingAudit[result.id]}
                            sx={{
                              borderRadius: 1.5,
                              textTransform: 'none',
                              fontWeight: 600,
                            }}
                          >
                            {restartingAudit[result.id] ? 'Queuing...' : 'Restart Audit'}
                          </Button>
                        </Box>

                        {Object.keys(checks).length > 0 ? (
                          <Stack spacing={2}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                              Health Checks
                            </Typography>
                            {WINDOWS_CHECK_KEYS.map((checkKey) => {
                              const check = checks[checkKey];
                              if (!check) return null;
                              const checkColor = getCheckColor(check.result);
                              return (
                                <Paper
                                  key={checkKey}
                                  sx={{
                                    p: 2,
                                    background: alpha(checkColor, 0.05),
                                    border: `1px solid ${alpha(checkColor, 0.2)}`,
                                    borderRadius: 1.5,
                                  }}
                                >
                                  <Stack direction="row" spacing={2} alignItems="flex-start">
                                    <Box
                                      sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: alpha(checkColor, 0.2),
                                        color: checkColor,
                                        flexShrink: 0,
                                      }}
                                    >
                                      {getCheckIcon(check.result)}
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }} flexWrap="wrap" useFlexGap>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                          {formatCheckName(checkKey)}
                                        </Typography>
                                        <Chip
                                          label={check.result}
                                          size="small"
                                          sx={{
                                            bgcolor: alpha(checkColor, 0.2),
                                            color: checkColor,
                                            fontWeight: 700,
                                          }}
                                        />
                                      </Stack>
                                      <Typography variant="body2" color="text.secondary">
                                        {check.message}
                                      </Typography>
                                    </Box>
                                  </Stack>
                                </Paper>
                              );
                            })}
                          </Stack>
                        ) : (
                          <Box sx={{ p: 3, textAlign: 'center' }}>
                            <AssessmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                            <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                              No Check Details
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              This endpoint has no recorded health check results
                            </Typography>
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  </Fade>
                );
              })}
          </Stack>
        ) : (
          !isLoading && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <AssessmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No audit results found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchTerm || statusFilter !== 'ALL'
                  ? 'Try adjusting your search or filter criteria'
                  : 'No Windows endpoint audit data is currently available'}
              </Typography>
            </Paper>
          )
        )}
      </Container>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default WindowsEndpointAudit;
